import { Sine, TimelineLite, TweenMax } from "gsap";
import * as HLF from "html-living-framework";
import * as Constants from "../Data/Constants";
import { OnboardingStep } from "../Data/OnboardingStep";
import * as SoundEffect from "../Data/SoundEffect";
import * as Topics from "../Data/Topics";
import { IOnboardingTarget } from "../GameObjects/IOnboardingTarget";

export class OnboardingManager extends PIXI.Container {
    private readonly _steps: OnboardingStep[] = new Array<OnboardingStep>();
    private _activeStep: OnboardingStep | undefined;
    private _activeStepIndex: number = -1;
    private readonly _arrowContainers: PIXI.Container[] = [];
    private readonly _arrows: PIXI.Sprite[] = [];
    private _arrowTimer: TimelineLite | undefined;
    private _arrowTween: TweenMax | undefined;
    private _messageSubscription: any;
    private _forceSubscription: any;

    constructor() {
        super();
    }

    public clear(): void {
        this._steps.length = 0;
    }

    public addStep(
        pEvent: string,
        pData: any,
        pTarget: IOnboardingTarget | IOnboardingTarget[],
        pDelay: number,
        pIsOrdered: boolean,
        pFailure?: string,
        pOnComplete?: () => void,
    ): void {
        if (Constants.DEBUG_FAST_ONBOARDING) {
            pDelay = 0.75;
        }
        this._steps.push(new OnboardingStep(pEvent, pData, pTarget, pDelay, pIsOrdered, pFailure, pOnComplete));
    }

    public start() {
        this._activeStepIndex = 0;
        this.beginStep();
        if (this._messageSubscription !== undefined) {
            PubSub.unsubscribe(this._messageSubscription);
        }
        if (this._forceSubscription !== undefined) {
            PubSub.unsubscribe(this._forceSubscription);
        }
        this._messageSubscription = PubSub.subscribe(Topics.CARMEN, this.onMessageReceived.bind(this));
        this._forceSubscription = PubSub.subscribe(Topics.FORCE_ONBOARDING_HINT, this.forceArrow.bind(this));
        PubSub.subscribe(Topics.FORCE_HIDE_ONBOARDING, this.hideArrows.bind(this));
    }

    public onResize(): void {
        if (this._activeStep !== undefined && this._arrowTween !== undefined && this._arrowTween.isActive) {
            this.hideArrows(true);
            if (this._arrowTimer !== undefined) {
                this._arrowTimer.kill();
            }
            this._arrowTimer = new TimelineLite();
            this._arrowTimer.call(this.showArrow.bind(this), undefined, this, 0.25);
        }
    }

    public cleanup(): void {
        PubSub.unsubscribe(this._messageSubscription);
        PubSub.unsubscribe(this._forceSubscription);
        this.hideArrows(true);
        this._arrowTween?.kill();
        this._arrowTimer?.kill();
    }

    private makeArrow() {
        const _arrow = HLF.PixiUtils.makeSprite("onboarding-arrow", Constants.SPRITESHEET_UI);
        _arrow.anchor.set(0.5, 1);
        _arrow.alpha = 0;
        _arrow.visible = false;

        const container = this.addChild(new PIXI.Container());
        this._arrowContainers.push(container);
        this.addChild(container);
        return container.addChild(_arrow);
    }

    private showArrow(): void {
        this._arrowTween?.kill();
        this._arrowTimer?.kill();
        this._arrowTimer = undefined;

        let targets = this._activeStep!.target;

        if (!Array.isArray(targets)) {
            targets = [targets];
        }

        while (targets.length > this._arrowContainers.length) {
            this._arrows.push(this.makeArrow()); // makeArrow adds to _arrowContainers
        }

        // if (targets.length === 0) {
        //     return;
        // }

        for (let i = 0; i < targets.length; i++) {
            const target = targets[i];
            const arrow = this._arrows[i];
            const arrowContainer = this._arrowContainers[i];
            const globalPos: PIXI.Point = target.getOnboardingPosition();
            const localPos: PIXI.Point = this.transform.worldTransform.applyInverse(globalPos);
            arrow.visible = true;
            arrowContainer.position.copy(localPos);
            arrow.position.y = -50;
            arrowContainer.rotation = target.getOnboardingRotation();
        }

        // hide any remaining arrows but don't actually destroy them
        for (let i = targets.length; i < this._arrows.length; i++) {
            const arrow = this._arrows[i];
            arrow.visible = false;
        }

        TweenMax.to(this._arrows, 0.5, {
            alpha: 1,
            ease: Sine.easeInOut,
        });
        this._arrowTween = new TweenMax(this._arrows, 1, {
            y: 0,
            ease: Sine.easeInOut,
            repeat: -1,
            yoyo: true,
        });
        if (Constants.DEBUG_MINIMAL_AUDIO === false) {
            if (targets.length > 0) {
                PubSub.publishSync(HLF.PLAY_AUDIO, new HLF.AudioToken(
                    SoundEffect.ONBOARDING_SHOW, 1, false, HLF.AudioCategory.SFX,
                ));
            }
        }
    }

    private hideArrows(pInstantly: boolean = false): void {
        if (this._arrowTween !== undefined) {
            this._arrowTween.kill();
        }
        TweenMax.to(this._arrows, pInstantly ? 0 : 0.25, {
            alpha: 0,
            ease: Sine.easeInOut,
            onComplete: () => {
                this._arrows.forEach((a) => {a.visible = false; });
            },
        });
    }

    private forceArrow(): void {
        if (this._arrowTimer !== undefined) {
            this._arrowTimer.kill();
        }
        this.showArrow();
    }

    private beginStep(pForceArrow: boolean = false): void {
        if (this._arrowTween !== undefined) {
            this._arrowTween.kill();
            this.hideArrows();
        }
        if (this._arrowTimer !== undefined) {
            this._arrowTimer.kill();
        }
        // Find the next uncompleted onboarding step
        while (this._activeStepIndex < this._steps.length && this._steps[this._activeStepIndex].isComplete) {
            ++this._activeStepIndex;
        }
        // If we are not yet done onboarding
        if (this._activeStepIndex < this._steps.length) {
            this._activeStep = this._steps[this._activeStepIndex];
            this._arrowTimer = new TimelineLite();
            this._arrowTimer.call(
                this.showArrow.bind(this),
                undefined, this, pForceArrow ? 0.75 : this._activeStep.hintDelay,
            );
        }
        // We have finished all onboarding steps
        else {
            PubSub.unsubscribe(this._messageSubscription);
            PubSub.unsubscribe(this._forceSubscription);
            this._messageSubscription = undefined;
            this._forceSubscription = undefined;
            this._activeStep = undefined;
            this._activeStepIndex = -1;
        }
    }

    private onMessageReceived(pTopic: string, pData?: any): void {
        // console.log(pTopic, pData);
        if (this._activeStep === undefined) {
            return;
        }
        // If we just performed the current onboarding step
        const correctAction: boolean = this._activeStep.event === pTopic;
        const correctData: boolean = this._activeStep.data === undefined || this._activeStep!.data === pData;
        const failAction: boolean = this._activeStep.failure === pTopic;
        if (correctAction && correctData) {
            this._activeStep.isComplete = true;
            if (this._activeStep.onComplete !== undefined) {
                this._activeStep.onComplete();
            }
            ++this._activeStepIndex;
            this.hideArrows();
            this.beginStep();
        }
        else if (failAction && correctData) {
            --this._activeStepIndex;
            this._steps[this._activeStepIndex].isComplete = false;
            this.hideArrows();
            this.beginStep(true);
        }
        // We didn't perform the current step, check to see if this was a later step
        else {
            for (let i = this._activeStepIndex + 1; i < this._steps.length; ++i) {
                if (
                    this._steps[i].isOrdered === false &&
                    this._steps[i].event === pTopic &&
                    (this._steps[i].data === undefined || this._steps[i].data === pData)
                ) {
                    console.log(`onboarding step ${this._steps[i].event} completed early`);
                    this._steps[i].isComplete = true;
                    break;
                }
            }
        }
    }
}
